import { IonButton, IonContent, IonHeader, IonIcon, IonItem, IonLabel, IonList, IonPage, IonText, IonTitle, IonToolbar, useIonAlert, useIonModal } from '@ionic/react';
import { IonButtons } from '@ionic/react';
import { modalController } from '@ionic/core';
import { isRfidLastSeenHidden, useRfidLastSeenByDeviceQuery } from '../query/rfidLastSeenQuery';
import { useDeleteRfidProfileMutation, useRfidProfileMutation, useRfidProfileQuery, useRfidProfilesQuery } from '../query/rfidProfileQuery';
import { useCurrentUserQuery } from '../query/currentUserQuery';
import { RfidProfile } from '../models/RfidProfile';
import { MouseEventHandler } from 'react';
import { addOutline, refreshOutline, trashOutline } from 'ionicons/icons';
import AddMicrochipModal, { ManualMicrochipEntry } from './AddMicrochipModal';
import MicrochipItemLabel from '../components/MicrochipItemLabel';
import RemoveIconButton from '../components/RemoveIconButton';
import useUX from '../utils/ux';
import { useCommonTranslation, useModalTranslation } from '../hooks/useTranslation';
import styles from './SelectRfidModal.module.css';

interface SeenRfidListItemProps {
    rfidCode: string;
    deviceId: string;
    timestamp?: Date | null;
    lastSeen?: string;
    disabled?: boolean;
    onClick?: MouseEventHandler<Element>
}

const SeenRfidListItem: React.FC<SeenRfidListItemProps> = ({ rfidCode, deviceId, timestamp, disabled, onClick }) => {
    const query = useRfidProfileQuery(rfidCode, deviceId);
    const { t } = useModalTranslation();

    return (
        <IonItem
            button={!disabled}
            detail={!disabled}
            onClick={disabled ? undefined : onClick}
            className={disabled ? styles.addedItem : undefined}
        >
            <MicrochipItemLabel
                rfidCode={rfidCode}
                label={query.data?.label}
                isLoading={query.isLoading}
                timestamp={timestamp}
            />
            {disabled && (
                <IonText slot="end" className={styles.addedLabel}>
                    {t('select_rfid.profiles.already_added')}
                </IonText>
            )}
        </IonItem>
    );
};

const RfidProfileModal = ({ dismiss, deviceId, disabledRfidCodes }: {
    dismiss: (rfidCode?: string, role?: string) => void,
    deviceId: string,
    disabledRfidCodes: string[]
}) => {
    const query = useRfidLastSeenByDeviceQuery(deviceId);
    const visibleItems = query.data?.filter((item) => !isRfidLastSeenHidden(item));
    const profilesQuery = useRfidProfilesQuery();
    const currentUserQuery = useCurrentUserQuery();
    const currentUserId = currentUserQuery.data?.id;
    const profileMutation = useRfidProfileMutation();
    const deleteProfileMutation = useDeleteRfidProfileMutation();
    const { presentResult } = useUX();
    const { t } = useModalTranslation();
    const { t: tCommon } = useCommonTranslation();
    const [presentAlert] = useIonAlert();

    const addMicrochips = (entries: ManualMicrochipEntry[]) => {
        presentResult(async () => {
            for (const entry of entries) {
                await profileMutation.mutateAsync({ rfidCode: entry.rfidCode, label: entry.name });
            }
        }, t('select_rfid.profiles.added', { count: entries.length }));
    };

    const [presentAddMicrochipModal, dismissAddMicrochipModal] = useIonModal(AddMicrochipModal, {
        dismiss: (entries: ManualMicrochipEntry[] | null, role: string) => {
            dismissAddMicrochipModal(entries, role);
            if (role === 'confirm' && entries?.length) {
                addMicrochips(entries);
            }
        },
        existingRfidCodes: profilesQuery.data?.map((profile) => profile.rfidCode) ?? []
    });

    const openAddMicrochipModal = async () => {
        const presentingElement = await modalController.getTop();
        presentAddMicrochipModal({ presentingElement: presentingElement ?? undefined });
    };

    // Deleting removes only the current user's own contribution row; pets
    // other household members added details for stay visible to them.
    const confirmRemoveProfile = (profile: RfidProfile) => {
        presentAlert({
            header: t('select_rfid.profiles.remove.header'),
            subHeader: profile.label || profile.rfidCode,
            message: t('select_rfid.profiles.remove.message'),
            buttons: [
                {
                    text: tCommon('buttons.cancel'),
                    role: 'cancel'
                },
                {
                    text: tCommon('buttons.delete'),
                    role: 'destructive',
                    handler: () => {
                        presentResult(
                            () => deleteProfileMutation.mutateAsync(profile.rfidCode),
                            t('select_rfid.profiles.remove.success')
                        );
                    }
                }
            ]
        });
    };

    return (
        <IonPage>
            <IonHeader translucent>
                <IonToolbar>
                    <IonButtons slot="start">
                        <IonButton color="medium" onClick={() => dismiss(undefined, 'cancel')}>
                            {tCommon('buttons.cancel')}
                        </IonButton>
                    </IonButtons>
                    <IonTitle>{t('select_rfid.title')}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton title={t('select_rfid.refresh')} onClick={() => query.refetch()} disabled={query.isFetching}>
                            <IonIcon icon={refreshOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent fullscreen color="light">
                <div className={styles.wrapper}>
                    <section className={styles.section}>
                        <div className={styles.sectionLabelRow}>
                            <h2 className={styles.sectionLabel}>{t('select_rfid.profiles.title')}</h2>
                            <button
                                type="button"
                                className={styles.sectionAction}
                                aria-label={t('select_rfid.profiles.add')}
                                onClick={openAddMicrochipModal}
                            >
                                <IonIcon icon={addOutline} aria-hidden="true" />
                                {t('select_rfid.profiles.new')}
                            </button>
                        </div>
                        <IonList className={styles.listCard}>
                            {profilesQuery.data?.map((profile) => {
                                const added = disabledRfidCodes?.includes(profile.rfidCode);
                                const canRemove = currentUserId != null && profile.contributedUserIds?.includes(currentUserId);
                                return (
                                    <IonItem
                                        key={profile.rfidCode}
                                        button={!added}
                                        detail={!added && !canRemove}
                                        onClick={added ? undefined : () => dismiss(profile.rfidCode, 'confirm')}
                                        className={added ? styles.addedItem : undefined}
                                    >
                                        <MicrochipItemLabel rfidCode={profile.rfidCode} label={profile.label} />
                                        {added && (
                                            <IonText slot="end" className={styles.addedLabel}>
                                                {t('select_rfid.profiles.already_added')}
                                            </IonText>
                                        )}
                                        {!added && canRemove && (
                                            <RemoveIconButton
                                                slot="end"
                                                icon={trashOutline}
                                                ariaLabel={t('select_rfid.profiles.remove.aria_label', { name: profile.label || profile.rfidCode })}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    confirmRemoveProfile(profile);
                                                }}
                                            />
                                        )}
                                    </IonItem>
                                );
                            })}
                            {profilesQuery.isSuccess && !profilesQuery.data?.length && (
                                <IonItem lines="none">
                                    <IonLabel color="medium" className="ion-text-wrap">{t('select_rfid.profiles.empty')}</IonLabel>
                                </IonItem>
                            )}
                        </IonList>
                    </section>
                    {!!visibleItems?.length && (
                        <section className={styles.section}>
                            <h2 className={styles.sectionLabel}>{t('select_rfid.seen.title')}</h2>
                            <IonList className={styles.listCard}>
                                {visibleItems.map(({ rfidCode, eventTimestamp }) => (
                                    <SeenRfidListItem key={rfidCode} rfidCode={rfidCode} deviceId={deviceId} timestamp={eventTimestamp} disabled={disabledRfidCodes?.includes(rfidCode)} onClick={() => dismiss(rfidCode, 'confirm')} />
                                ))}
                            </IonList>
                        </section>
                    )}
                    <p className={styles.note}>{t('select_rfid.seen.note')}</p>
                </div>
            </IonContent>
        </IonPage>
    );
};

export default RfidProfileModal;
